import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Box, Stack, Typography } from "@mui/material";
import { Flex } from "Components/_Shared/Core/Flex";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { formatPercentage } from "Infrastructure/Utils/NumberUtils";
import { type NsureSelectionItem, NsureSelectionItemType } from "./InvestmentsNsureSelectionContext";
import { formatSrriSri } from "./utils";

type Props = {
  readonly selection: NsureSelectionItem;
};

export const InvestmentsNsureSelectionHeader = ({ selection }: Props) => {
  const { t, resolveResourcePath } = useResource();

  const isPortfolio = selection.type === NsureSelectionItemType.Portfolio;
  const strategyRiskType = isPortfolio ? selection.strategyRiskType : null;
  const isin = isPortfolio ? null : selection.isin;

  const subtitle = strategyRiskType
    ? `${t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.PortfolioCard.Category)} ${t(
        resolveResourcePath(`Resources.AnalysisDetail.GoalsFulfillment.Nsure.StrategyRiskType.${strategyRiskType}`),
      )}`
    : isin
      ? `${t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Labels.Isin)}: ${isin}`
      : null;

  return (
    <Stack spacing={Spacing.M}>
      <Flex justifyContent="space-between" alignItems="center" gap={Spacing.S}>
        <Typography variant="body1" fontWeight={700} color="primary.main">
          {selection.name}
        </Typography>
        <CheckCircleIcon sx={{ color: "primary.main", fontSize: 20, flexShrink: 0 }} />
      </Flex>

      {subtitle && (
        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>
      )}

      <Box
        display="grid"
        gridTemplateColumns="auto auto 1fr"
        columnGap={Spacing.L}
        px={Spacing.L}
        py={Spacing.M}
        bgcolor="grey.50"
        borderRadius={Radius.M}
        sx={{ "& > * + *": { borderLeft: "1px solid", borderColor: "divider", pl: Spacing.L } }}
      >
        <SelectionMetric
          label={t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Labels.SrriSri)}
          value={formatSrriSri(selection.srri, selection.sri)}
        />
        <SelectionMetric
          label={t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Labels.Esg)}
          value={selection.esg != null ? formatPercentage(selection.esg) : "—"}
        />
        <SelectionMetric
          label={t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Labels.ExpectedPerformance)}
          value={selection.expectedPerformance != null ? formatPercentage(selection.expectedPerformance) : "—"}
        />
      </Box>
    </Stack>
  );
};

const SelectionMetric = ({ label, value }: { readonly label: string; readonly value: string }) => (
  <Stack height="100%" minWidth={0} justifyContent="space-between" spacing={Spacing.XS}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={600} noWrap>
      {value}
    </Typography>
  </Stack>
);
